export type DocLevel = 'customer' | 'vorgang';

/** Spiegelt api/src/files/document-levels.ts: Dauer-Dokumente (Vollmacht/GRC/
 *  De-Minimis, inkl. "… – Unterschrieben") = Kunde; alles andere = Vorgang. */
export function levelForCategory(category: string | null | undefined): DocLevel {
  const c = (category || '').toLowerCase();
  if (c.includes('vollmacht') || c.includes('grc') || c.includes('minimis')) return 'customer';
  return 'vorgang';
}

/** Spiegelt api/src/files/document-naming.ts: sichtbare Bezeichnung einer Dokumentart.
 *  'Bericht PDF' und 'Rechnung' bleiben intern (Signatur-Marker, Abfragen, laufende
 *  Unterschriftsrunden hängen daran); angezeigt und in Dateinamen geschrieben wird
 *  „Beratungsbericht" bzw. „Beratungsrechnung". Zusätze wie „– Unterschrieben" bleiben. */
const SICHTBARE_NAMEN: Array<{ intern: string; sichtbar: string }> = [
  { intern: 'bericht pdf', sichtbar: 'Beratungsbericht' },
  { intern: 'rechnung', sichtbar: 'Beratungsrechnung' },
];
export function dokumentLabel(category: string | null | undefined): string {
  const roh = (category || '').trim();
  const lc = roh.toLowerCase();
  for (const { intern, sichtbar } of SICHTBARE_NAMEN) {
    if (lc === intern) return sichtbar;
    if (lc.startsWith(intern)) {
      const rest = roh.slice(intern.length);
      if (/^\s*[–—-]/.test(rest)) return sichtbar + rest;
    }
  }
  return roh;
}

/** Erwartete Dokumenttypen (Soll-Satz) je Spalte für die X/Y-Vollständigkeit.
 *  Grundtypen — „vorhanden" zählt egal ob unterschrieben oder nicht (siehe
 *  DocumentGrid: Vergleich ohne „– Unterschrieben"-Suffix, case-insensitiv). */
export const VORGANG_SOLL = ['Antrag', 'Erlaubnis', 'ESF Fragebogen', 'Bericht PDF', 'Verwendungsnachweis', 'Rechnung', 'Kontoauszug'];
export const DAUER_SOLL = ['Vollmacht', 'GRC', 'De-Minimis'];

/** Grundtyp einer Kategorie: ohne „– Unterschrieben"-Suffix, klein geschrieben.
 *  So zählt „Vollmacht" UND „Vollmacht – Unterschrieben" als Soll-Typ „Vollmacht". */
export function baseCategory(c: string | null | undefined): string {
  return (c || '').replace(/\s*[–-]\s*(vollständig\s+)?unterschrieben\s*$/i, '').trim().toLowerCase();
}

/** Vollständigkeit eines Soll-Satzes gegen eine Dateiliste — dieselbe Rechnung wie im
 *  DocumentGrid, damit die Kopfzeile eines Vorgangs ohne Aufklappen denselben Stand zeigt. */
export function sollStand(
  files: Array<{ category: string | null }> | undefined,
  soll: string[],
): { vorhanden: number; gesamt: number; fehlend: string[] } {
  const present = new Set((files ?? []).map(f => baseCategory(f.category)));
  const fehlend = soll.filter(s => !present.has(baseCategory(s)));
  return { vorhanden: soll.length - fehlend.length, gesamt: soll.length, fehlend };
}

/** Wählbare Dokumenttypen beim Upload je Ebene.
 *  „BAFA Formular" ist bewusst NICHT dabei: das sind die generierten Signier-
 *  Quellformulare (Vollmacht/GRC/De-Minimis), die nur in der „Unterschreiben"-
 *  Karte leben; unterschrieben landen sie als Dauer-Dokumente beim Kunden. */
export const VORGANG_CATEGORIES = ['Antrag', 'Erlaubnis', 'ESF Fragebogen', 'Bericht PDF', 'Verwendungsnachweis', 'Rechnung', 'Kontoauszug', 'Zuwendungsbescheid', 'Sonstiges'];
export const DAUER_CATEGORIES = ['Vollmacht', 'GRC', 'De-Minimis', 'Sonstiges'];
